import React from 'react';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from '@mui/lab';
import {
  Box,
  Typography,
  Paper,
  Chip,
} from '@mui/material';
import {
  Receipt as ReceiptIcon,
  Edit as EditIcon,
  Send as SendIcon,
  ArrowDownward as ArrowDownwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
} from '@mui/icons-material';
import { BillEvent, BillEventType, getEventColor } from '@/types/billEvent';
import { billEventService } from '@/services/billEventService';
import { useI18n } from '@/hooks/useI18n';

interface BillTimelineProps {
  events: BillEvent[];
  showDetails?: boolean;
}

const BillTimeline: React.FC<BillTimelineProps> = ({ events, showDetails = true }) => {
  const { t } = useI18n();

  // Sort events chronologically (newest first)
  const sortedEvents = billEventService.sortEventsByTimestamp(events, false);

  const getEventIcon = (eventType: BillEventType) => {
    switch (eventType) {
      case BillEventType.BILL_CREATED:
        return <ReceiptIcon />;
      case BillEventType.BILL_CHANGED:
        return <EditIcon />;
      case BillEventType.BILL_SENT:
        return <SendIcon />;
      case BillEventType.PAYMENT_RECEIVED:
        return <ArrowDownwardIcon />;
      case BillEventType.PAYMENT_SENT:
        return <ArrowUpwardIcon />;
      case BillEventType.FULLY_PAID:
        return <CheckCircleIcon />;
      default:
        return <EventIcon />;
    }
  };

  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatDateTime = (date: Date): string => {
    return new Intl.DateTimeFormat('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const getEventDetails = (event: BillEvent): React.ReactNode => {
    if (!showDetails) return null;

    switch (event.type) {
      case BillEventType.BILL_CREATED:
        return (
          <Box sx={{ mt: 1 }}>
            {event.triggeredByName && (
              <Typography variant="body2" color="text.secondary">
                {t('bill.eventDetails.createdBy')}: {event.triggeredByName}
              </Typography>
            )}
          </Box>
        );
      
      case BillEventType.BILL_CHANGED:
        return (
          <Box sx={{ mt: 1 }}>
            {event.triggeredByName && (
              <Typography variant="body2" color="text.secondary">
                {t('bill.eventDetails.changedBy')}: {event.triggeredByName}
              </Typography>
            )}
            {event.details?.changedFields && Array.isArray(event.details.changedFields) && event.details.changedFields.length > 0 && (
              <Typography variant="body2" color="text.secondary">
                {t('bill.eventDetails.changedFields')}: {event.details.changedFields.join(', ')}
              </Typography>
            )}
          </Box>
        );
      
      case BillEventType.BILL_SENT:
        return (
          <Box sx={{ mt: 1 }}>
            {event.triggeredByName && (
              <Typography variant="body2" color="text.secondary">
                {t('bill.eventDetails.sentBy')}: {event.triggeredByName}
              </Typography>
            )}
            {event.details?.deliveryMethod && (
              <Typography variant="body2" color="text.secondary">
                {t('bill.eventDetails.deliveryMethod')}: {String(event.details.deliveryMethod)}
              </Typography>
            )}
          </Box>
        );
      
      case BillEventType.PAYMENT_RECEIVED:
        return (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="success.main" fontWeight="bold">
              {t('bill.eventDetails.amount')}: {formatCurrency(event.details?.amount as number || 0)}
            </Typography>
            {event.details?.paymentMethod && (
              <Typography variant="body2" color="text.secondary">
                {t('bill.eventDetails.paymentMethod')}: {String(event.details.paymentMethod)}
              </Typography>
            )}
            {event.details?.reference && (
              <Typography variant="body2" color="text.secondary">
                {t('bill.eventDetails.reference')}: {String(event.details.reference)}
              </Typography>
            )}
          </Box>
        );
      
      case BillEventType.PAYMENT_SENT:
        return (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="error.main" fontWeight="bold">
              {t('bill.eventDetails.amount')}: {formatCurrency(event.details?.amount as number || 0)}
            </Typography>
            {event.details?.recipient && (
              <Typography variant="body2" color="text.secondary">
                {t('bill.eventDetails.recipient')}: {String(event.details.recipient)}
              </Typography>
            )}
            {event.details?.paymentMethod && (
              <Typography variant="body2" color="text.secondary">
                {t('bill.eventDetails.paymentMethod')}: {String(event.details.paymentMethod)}
              </Typography>
            )}
          </Box>
        );
      
      case BillEventType.FULLY_PAID:
        return (
          <Box sx={{ mt: 1 }}>
            <Typography variant="body2" color="success.main" fontWeight="bold">
              {t('bill.eventDetails.totalAmount')}: {formatCurrency(event.details?.totalAmount as number || 0)}
            </Typography>
            {event.details?.finalPaymentAmount && (
              <Typography variant="body2" color="text.secondary">
                {t('bill.eventDetails.finalPayment')}: {formatCurrency(event.details.finalPaymentAmount as number)}
              </Typography>
            )}
          </Box>
        );
      
      default:
        return null;
    }
  };

  if (sortedEvents.length === 0) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          {t('bill.noEventsAvailable')}
        </Typography>
      </Box>
    );
  }

  return (
    <Timeline position="right">
      {sortedEvents.map((event, index) => (
        <TimelineItem key={event.id}>
          <TimelineOppositeContent
            sx={{ m: 'auto 0' }}
            align="right"
            variant="body2"
            color="text.secondary"
          >
            {formatDateTime(event.timestamp)}
          </TimelineOppositeContent>
          <TimelineSeparator>
            <TimelineDot color={getEventColor(event.type)}>
              {getEventIcon(event.type)}
            </TimelineDot>
            {index < sortedEvents.length - 1 && <TimelineConnector />}
          </TimelineSeparator>
          <TimelineContent sx={{ py: '12px', px: 2 }}>
            <Paper
              elevation={1}
              sx={{
                p: 2,
                backgroundColor: 'background.paper'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="h6" component="span">
                  {t(`bill.eventTypes.${event.type}`)}
                </Typography>
                <Chip 
                  size="small" 
                  label={t(`bill.eventTypes.${event.type}`)} 
                  color={getEventColor(event.type)}
                  variant="outlined"
                />
              </Box>
              {getEventDetails(event)}
            </Paper>
          </TimelineContent>
        </TimelineItem>
      ))}
    </Timeline>
  );
};

export default BillTimeline;
